import { useAuthStore } from "~/stores/useAuthStore";
import { useTrackingContextStore } from "~/stores/useTrackingContextStore";
import {
  takePointConsumeAttribution,
  takeVideoCallAttribution,
} from "~/utils/demoCallAttribution";
import { trackAdjustEvent } from "./adjustTracker";
import { EVENT_NAMES } from "./eventNames";
import { EVENT_TRACKING_CONFIG } from "./eventTrackingConfig";
import { trackReproEvent } from "./reproTracker";

export { EVENT_NAMES, type EventName } from "./eventNames";
export type { AdjustPlatform } from "./platform";
export type { ReproEventProps } from "./reproTracker";
export { TRACKING_SOURCE, type TrackingSource } from "./trackingSource";

/**
 * Repro と Adjust に同時にイベントを送信する統合エントリーポイント。
 *
 * - Repro: 常に送信（ネイティブアプリのみ）
 * - Adjust: ADJUST_TOKENS に定義済みのイベント・プラットフォームのみ送信
 *
 * EVENT_TRACKING_CONFIG に設定があるイベントは、呼び出し前に
 * useTrackingContextStore.setSource() / setPointsConsumed() でセットした値を自動参照する。
 * - useSource: true → `${eventName}_${source}` のサフィックスイベントを追加送信
 * - usePointsConsumed: true → `${eventName}_${pointsConsumed}` のサフィックスイベントを追加送信
 * 参照後はストアが自動でクリアされる（単一消費）。
 *
 * 例: source="FROM_MESSAGE", pointsConsumed=50 の場合、
 *   COMPLETE_SEND_GIFT / COMPLETE_SEND_GIFT_FROM_MESSAGE / COMPLETE_SEND_GIFT_50 の3本を送信。
 *
 * demoAttribution: true → デモ通話応答時に armDemoCallAttribution() で立てたフラグを
 * 消費し、対象なら `${eventName}_AFTER_DEMO_CALL`（video/consume 共通・重複時は1回）と
 * CONSUME_POINT_AFTER_DEMO_CALL（consume のみ）を Repro にのみ追加送信する（Adjust には送らない）。
 */
export const trackEvent = (eventName: string): void => {
  const config = EVENT_TRACKING_CONFIG[eventName];
  const ctx = useTrackingContextStore.getState();

  trackReproEvent(eventName);

  if (config) {
    if (config.useSource && ctx.source) {
      trackReproEvent(`${eventName}_${ctx.source}`);
    }
    if (config.usePointsConsumed && ctx.pointsConsumed !== null) {
      trackReproEvent(`${eventName}_${ctx.pointsConsumed}`);
    }
    ctx.clear();
  }

  const userId = useAuthStore.getState().userId;
  if (config?.demoAttribution && userId) {
    const videoFired =
      config.demoAttribution.includes("video") &&
      takeVideoCallAttribution(userId);
    const consumeFired =
      config.demoAttribution.includes("consume") &&
      takePointConsumeAttribution(userId);
    if (videoFired || consumeFired) {
      trackReproEvent(`${eventName}_AFTER_DEMO_CALL`);
    }
    if (consumeFired) {
      trackReproEvent(EVENT_NAMES.CONSUME_POINT_AFTER_DEMO_CALL);
    }
  }

  trackAdjustEvent(eventName);
};
